import { StyleSheet, Platform } from 'react-native'
import type { Theme } from '../../../theme/getTheme'

interface RegistrationFormStyleOptions {
  marketing?: boolean
  /** Preferred over inferring palette from gray tokens (marketing light vs dark). */
  isDark?: boolean
}

/** Placeholder hue tuned for contrast on elevated field surfaces (especially dark mode). */
export function registrationFormPlaceholderColor(theme: Theme, isDark: boolean): string {
  return isDark ? theme.gray[600] : theme.gray[400]
}

export function getRegistrationFormStyles(
  theme: Theme,
  options: RegistrationFormStyleOptions = {},
) {
  const marketing = options.marketing === true
  const isDarkTheme =
    options.isDark === true ||
    (options.isDark == null && theme.gray[900].toUpperCase() === '#F9FAFB')
  const ink = theme.gray[900]
  const body = theme.gray[600]
  const label = marketing && isDarkTheme ? theme.gray[800] : theme.gray[700]
  const subtle = theme.gray[200]
  // TODO(theme): add light-mode marketing border / panel tokens for these literals
  // ('rgba(159, 122, 234, 0.24)' light-violet border, '#FAF5FF' soft violet panel).
  const border = marketing ? (isDarkTheme ? theme.gray[300] : 'rgba(159, 122, 234, 0.24)') : theme.gray[300]
  /** Dark marketing: slightly stronger edge so fields read clearly on `cardElevated`. */
  const fieldBorder = marketing && isDarkTheme ? theme.gray[400] : border
  const fieldBg = marketing
    ? isDarkTheme
      ? theme.background.cardElevated
      : theme.white
    : theme.background.card
  const chipBg = marketing ? (isDarkTheme ? theme.background.cardElevated : theme.white) : theme.background.card
  // TODO(theme): light marketing panel uses '#FAF5FF' — add a soft-violet panel token.
  const panelBg = marketing ? (isDarkTheme ? theme.background.shell : '#FAF5FF') : theme.background.card
  const primary = theme.primary
  /** Outline fields so RN Web + light marketing keep visible bounds. */
  const inputOutline = 1

  return StyleSheet.create({
    container: {
      width: '100%' as never,
      maxWidth: marketing ? 680 : 520,
      alignSelf: 'center' as const,
    },
    sectionTitle: {
      fontSize: 16,
      fontWeight: '700',
      color: ink,
      marginBottom: 12,
      marginTop: 20,
    },
    sectionTitleFirst: {
      fontSize: 16,
      fontWeight: '700',
      color: ink,
      marginBottom: 12,
      marginTop: 0,
    },
    inviteSubtitle: {
      fontSize: 14,
      lineHeight: 20,
      color: body,
      marginBottom: 12,
    },
    label: {
      fontSize: 14,
      fontWeight: '600',
      color: label,
      marginBottom: 4,
    },
    input: {
      borderWidth: inputOutline,
      borderColor: fieldBorder,
      borderRadius: marketing ? 14 : 10,
      paddingHorizontal: 14,
      paddingVertical: 12,
      fontSize: 16,
      color: ink,
      backgroundColor: fieldBg,
      marginBottom: 12,
      ...((Platform.OS === 'web' &&
        marketing && {
          outlineStyle: 'none',
          outlineWidth: 0,
        }) as any),
    } as any,
    textArea: {
      borderWidth: inputOutline,
      borderColor: fieldBorder,
      borderRadius: marketing ? 14 : 10,
      paddingHorizontal: 14,
      paddingVertical: 12,
      fontSize: 16,
      color: ink,
      backgroundColor: fieldBg,
      marginBottom: 12,
      minHeight: 80,
      textAlignVertical: 'top' as const,
      ...((Platform.OS === 'web' && { verticalAlign: 'top' }) as any),
      ...((Platform.OS === 'web' &&
        marketing && {
          outlineStyle: 'none',
          outlineWidth: 0,
        }) as any),
    } as any,
    /**
     * RN Web often drops background/border on `<input>`; outer shell keeps bounds visible.
     * Used only when `marketing && Platform.OS === 'web'`.
     */
    marketingWebFieldShell: {
      alignSelf: 'stretch',
      borderWidth: 1,
      borderColor: fieldBorder,
      borderRadius: marketing ? 14 : 10,
      backgroundColor: fieldBg,
      paddingHorizontal: 14,
      paddingVertical: 12,
      marginBottom: 12,
    },
    marketingWebFieldInput: {
      borderWidth: 0,
      paddingHorizontal: 0,
      paddingVertical: 0,
      margin: 0,
      fontSize: 16,
      color: ink,
      backgroundColor: 'transparent',
      ...((Platform.OS === 'web' && {
        outlineStyle: 'none',
        outlineWidth: 0,
      }) as any),
    } as any,
    marketingWebTextAreaShell: {
      alignSelf: 'stretch',
      borderWidth: 1,
      borderColor: fieldBorder,
      borderRadius: marketing ? 14 : 10,
      backgroundColor: fieldBg,
      paddingHorizontal: 14,
      paddingVertical: 12,
      marginBottom: 12,
      minHeight: 80,
    },
    marketingWebTextAreaInput: {
      borderWidth: 0,
      paddingHorizontal: 0,
      paddingVertical: 0,
      margin: 0,
      minHeight: 56,
      fontSize: 16,
      color: ink,
      backgroundColor: 'transparent',
      textAlignVertical: 'top' as const,
      ...((Platform.OS === 'web' && {
        verticalAlign: 'top',
        outlineStyle: 'none',
        outlineWidth: 0,
      }) as any),
    } as any,
    pickerRow: {
      flexDirection: 'row' as const,
      flexWrap: 'wrap' as const,
      gap: 8,
      marginBottom: 12,
    },
    pickerChip: {
      paddingVertical: 8,
      paddingHorizontal: 14,
      borderRadius: 20,
      borderWidth: 1,
      borderColor: fieldBorder,
      backgroundColor: chipBg,
    },
    pickerChipActive: {
      borderColor: primary,
      // TODO(theme): light marketing uses '#F3EDFF' — add a light-violet chip-active token.
      backgroundColor: marketing ? (isDarkTheme ? theme.primary + '33' : '#F3EDFF') : theme.primary + '22',
    },
    pickerChipText: {
      fontSize: 13,
      fontWeight: '600',
      color: body,
    },
    pickerChipTextActive: {
      color: primary,
    },
    colorRow: {
      flexDirection: 'row' as const,
      gap: 12,
      marginBottom: 12,
    },
    colorInputWrapper: {
      flex: 1,
    },
    colorPreview: {
      width: 28,
      height: 28,
      borderRadius: 14,
      borderWidth: 1,
      borderColor: theme.gray[300],
      marginLeft: 8,
      alignSelf: 'center' as const,
    },
    colorInputRow: {
      flexDirection: 'row' as const,
      alignItems: 'center' as const,
    },
    colorInputField: {
      flex: 1,
      marginBottom: 0,
    },
    divider: {
      height: 1,
      backgroundColor: subtle,
      marginVertical: 16,
    },
    joinSection: {
      marginTop: 20,
      padding: marketing ? 18 : 14,
      borderRadius: marketing ? 18 : 12,
      backgroundColor: panelBg,
      borderWidth: marketing ? 0 : 1,
      borderColor: border,
    },
    joinSectionInviteFirst: {
      marginTop: 0,
      padding: marketing ? 18 : 14,
      borderRadius: marketing ? 18 : 12,
      backgroundColor: panelBg,
      borderWidth: marketing ? 0 : 1,
      borderColor: border,
    },
    joinLabel: {
      fontSize: 14,
      fontWeight: '600',
      color: label,
      marginBottom: 8,
    },
    submitRow: {
      flexDirection: 'row' as const,
      gap: 12,
      flexWrap: 'wrap' as const,
      justifyContent: 'center' as const,
      marginTop: 16,
    },
    primaryBtn: {
      backgroundColor: primary,
      paddingVertical: 14,
      paddingHorizontal: 24,
      borderRadius: marketing ? 14 : 10,
    },
    primaryBtnDisabled: {
      opacity: 0.5,
    },
    primaryBtnText: {
      color: theme.white,
      fontWeight: '700',
      fontSize: 16,
      textAlign: 'center' as const,
    },
    secondaryBtn: {
      backgroundColor: marketing && isDarkTheme ? theme.background.cardElevated : theme.white,
      borderWidth: 1,
      borderColor: fieldBorder,
      paddingVertical: 14,
      paddingHorizontal: 24,
      borderRadius: marketing ? 14 : 10,
    },
    secondaryBtnText: {
      // TODO(theme): light marketing uses '#553C9A' — add a deep-violet on-light text token.
      color: marketing ? (isDarkTheme ? theme.primary : '#553C9A') : theme.gray[700],
      fontWeight: '700',
      fontSize: 16,
      textAlign: 'center' as const,
    },
    errorText: {
      color: theme.error,
      marginTop: 8,
      textAlign: 'center' as const,
    },
    pressed: {
      opacity: 0.85,
    },
  })
}

export function colorPreviewBgStyle(color: string) {
  return { backgroundColor: color } as const
}
